import { string } from 'prop-types';
import React, { useState } from 'react';
import { Popover, Position, Text, Box, Stack, Panel, TextLink } from '@chaser/components';
import useFetch from '../../hooks/useFetch';
import useMedia from '../../hooks/useMedia';
import usePush from '../../hooks/usePush';
import useUpdateEffect from '../../hooks/useUpdateEffect';
import NotificationService from '../../services/notification-service';
import LoadingMessage from '../LoadingMessage/index';
import NotificationBell from '../NotificationBell/index';
import CollectionsNotification from './NotificationCard/Collections/index';
import MentionsNotification from './NotificationCard/Mentions/index';
import ReminderNotification from './NotificationCard/Reminder/index';
import NotificationCard from './NotificationCard/index';
import style from './Notifications.module.scss';

const NotificationsPanel = ({ shortCode }) => {
  const [isNotifPanelOpen, setIsNotifPanelOpen] = useState(false);
  const {
    data,
    loading,
    refetch,
    applyOptimisticUpdate,
  } = useFetch(NotificationService.getNotifications, shortCode, { suspense: false });

  const notifications = data?.notifications;
  const totalUnseenNotifications = data?.totalUnseenNotifications;

  useUpdateEffect(
    didUpdate => {
      if (didUpdate && isNotifPanelOpen) refetch();
    },
    [isNotifPanelOpen],
  );

  const isMobile = useMedia('(max-width: 1000px)');

  const { execute } = usePush(async () => {
    await NotificationService.markAllNotificationsAsSeen(shortCode);
    await refetch();
  });

  async function updateAllAsSeen(e) {
    e.stopPropagation();
    await applyOptimisticUpdate(state => {
      state.notifications = state.notifications.map(notif => ({
        ...notif,
        seen: true,
      }));
    }, execute());
  }

  return (
    <Popover
      position={isMobile ? Position.BOTTOM_LEFT : Position.BOTTOM_RIGHT}
      onToggle={setIsNotifPanelOpen}
      target={<NotificationBell totalUnseenNotifications={totalUnseenNotifications} />}
      contentClassName={style.popover}
    >
      <Panel
        header={
          <>
            <Text as="h1" fontWeight="bold">
              Notifications
            </Text>
            <Text>
              <TextLink
                as="button"
                type="button"
                color="Mid Neutral 1"
                onClick={e => updateAllAsSeen(e)}
              >
                Mark all as read
              </TextLink>
            </Text>
          </>
        }
      >
        <div className={style.notificationContent}>
          <Box p="xsmall">
            {loading && !notifications && <LoadingMessage p={0} overlay />}
            {!loading && (!notifications || notifications.length === 0) && (
              <Stack className={style.centerAlign}>
                <span aria-label="gratz" role="img" className={style.tadaIcon}>
                  🎉
                </span>
                <Text color="Mid Neutral 1">You&apos;re all caught up!</Text>
              </Stack>
            )}
            {notifications && (
              <Stack gap="xsmall">
                {notifications.map(notification => {
                  switch (notification.type) {
                    case 'collections':
                      return (
                        <CollectionsNotification
                          key={notification._id}
                          notification={notification}
                          refetch={refetch}
                          shortCode={shortCode}
                          applyOptimisticUpdate={applyOptimisticUpdate}
                        />
                      );
                    case 'mention':
                      return (
                        <MentionsNotification
                          key={notification._id}
                          notification={notification}
                          refetch={refetch}
                          shortCode={shortCode}
                          applyOptimisticUpdate={applyOptimisticUpdate}
                        />
                      );
                    case 'reminder':
                      return (
                        <ReminderNotification
                          key={notification._id}
                          notification={notification}
                          refetch={refetch}
                          shortCode={shortCode}
                          applyOptimisticUpdate={applyOptimisticUpdate}
                        />
                      );
                    default:
                      return (
                        <NotificationCard
                          key={notification._id}
                          notification={notification}
                          refetch={refetch}
                          shortCode={shortCode}
                          applyOptimisticUpdate={applyOptimisticUpdate}
                          onClick={() => {}}
                          title="Unsupported notification type"
                          subtitle="This notification is unsupported"
                        />
                      );
                  }
                })}
              </Stack>
            )}
          </Box>
        </div>
      </Panel>
    </Popover>
  );
};

export default NotificationsPanel;

NotificationsPanel.propTypes = {
  shortCode: string.isRequired,
};
